import { alpha, Box, List, Typography } from "@mui/material";
import useDictionaries from "features/dictionaries/useDictionaries";
import { ProgramItem } from "features/programs/programApi";
import moment from "moment";
import { useMemo } from "react";
import { KitchenItem, Summary } from "./kitchenApi";
import KitchenListItem from "./KitchenListItem";
import OrderItem from "./OrderItem";

type KitchenStatus = "finished" | "running" | "next" | "upcoming";

interface KitchenListProps {
  kitchenData: KitchenItem[];
}

interface KitchenItemWithStatus extends KitchenItem {
  status: KitchenStatus;
}

const summaryColors = {
  option: "lightblue",
  variant: "lightgreen",
};

export default function KitchenList({ kitchenData }: KitchenListProps) {
  const { resolveOption } = useDictionaries();

  const kitchenWithStatus = useMemo(() => {
    const now = moment();
    const today = moment().startOf("day");

    const withStatus: KitchenItemWithStatus[] = kitchenData.map(item => {
      const startTime = moment(item.start_time);

      // Calculate total program duration
      let totalDuration = 0;
      item.program_ids.forEach(programId => {
        const program = resolveOption<ProgramItem>("programs", programId);
        if (program?.duration) {
          totalDuration = Math.max(totalDuration, program.duration);
        }
      });

      const endTime = startTime.clone().add(totalDuration, "minutes");

      let status: KitchenStatus = "upcoming";

      if (endTime.isBefore(now)) {
        status = "finished";
      } else if (startTime.isBefore(now) && endTime.isAfter(now)) {
        status = "running";
      } else if (startTime.isAfter(now)) {
        // Only mark as "next" if it's the same day
        if (startTime.isSame(today, "day")) {
          status = "next";
        } else {
          status = "upcoming";
        }
      }

      return {
        ...item,
        status,
      };
    });

    // First, find the earliest "next" departure time
    const nextItems = withStatus.filter(item => item.status === "next");
    if (nextItems.length > 0) {
      const earliestNextTime = moment.min(
        nextItems.map(item => moment(item.start_time))
      );

      // Mark items that start within 30 minutes of the earliest as "next"
      withStatus.forEach(item => {
        if (item.status === "next") {
          const itemStartTime = moment(item.start_time);
          const timeDiff = itemStartTime.diff(earliestNextTime, "minutes");
          if (timeDiff > 30) {
            item.status = "upcoming";
          }
        }
      });
    }

    // Sort: running first, then next, then upcoming, then finished
    const statusOrder = { running: 0, next: 1, upcoming: 2, finished: 3 };
    return withStatus.sort((a, b) => {
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;

      // Within same status, sort by start_time
      return moment(a.start_time).diff(moment(b.start_time));
    });
  }, [kitchenData, resolveOption]);

  // Group and sum the summaries by name
  const groupedSummaries = kitchenWithStatus
    .flatMap(item => item.summary)
    .reduce((acc, summary) => {
      const existing = acc.find(s => s.name === summary.name);
      if (existing) {
        existing.qty += summary.qty;
      } else {
        acc.push({ ...summary });
      }
      return acc;
    }, [] as Summary[]);
  groupedSummaries.sort((a, b) => b.qty - a.qty);
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {kitchenData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            flexWrap: "wrap",
            p: 1,
            borderRadius: "6px",
            border: theme => `solid 1px ${alpha(theme.palette.info.main, 0.4)}`,
            backgroundColor: theme => alpha(theme.palette.info.main, 0.1),
          }}
        >
          <Typography variant="body1" align="center">
            {groupedSummaries.length > 0
              ? "Időszak összes rendelése"
              : "Nincs rendelés az időszakra"}
          </Typography>
          {groupedSummaries.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "row",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {groupedSummaries.map((summary, index) => (
                <OrderItem
                  key={index}
                  name={summary.name}
                  qty={summary.qty}
                  type={summary.type}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
      <List disablePadding dense>
        {kitchenWithStatus.map(item => (
          <KitchenListItem
            key={`${item.boat_id}-${item.start_time}`}
            item={item}
            status={item.status}
          />
        ))}
      </List>
    </Box>
  );
}
